import { useEffect, useRef, useState } from 'react';
import { Check, Plus } from 'lucide-react';

// Combobox de etiqueta libre: filtra según escribes y ofrece "Crear …" cuando no
// hay coincidencia exacta. Portado de maman-tax (la contabilidad de la madre de
// Adrien), adaptado a la paleta de Habitania. `options` son las categorías ya
// usadas (se derivan del historial), pero el campo nunca deja de ser libre.
interface Props {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export default function CategoryCombobox({
  value,
  options,
  onChange,
  placeholder = 'Selecciona o crea una categoría',
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    setQuery('');
  }, [value]);

  const lowered = query.trim().toLowerCase();
  const filtered = lowered
    ? options.filter((o) => o.toLowerCase().includes(lowered))
    : options;
  const exactMatch = options.some((o) => o.toLowerCase() === lowered);
  const canCreate = lowered.length > 0 && !exactMatch;

  const pick = (next: string) => {
    onChange(next);
    setQuery('');
    setOpen(false);
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <div
        onClick={() => setOpen(true)}
        className="flex w-full cursor-text items-center gap-1 rounded-xl border border-deep/15 bg-surface px-3 py-2"
      >
        {!open && value ? (
          <span className="flex-1 text-sm text-deep">{value}</span>
        ) : (
          <input
            autoFocus={open}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={value || placeholder}
            className="flex-1 bg-transparent text-sm text-deep outline-none placeholder:text-ink/40"
          />
        )}
        {value && (
          <button
            type="button"
            onClick={clear}
            className="px-1 text-xs text-ink/45 hover:text-deep"
            tabIndex={-1}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-xl border border-deep/10 bg-white text-sm shadow-lg">
          {filtered.length === 0 && !canCreate && (
            <div className="px-3 py-2 text-ink/50">
              No hay categorías todavía. Escribe una para crearla.
            </div>
          )}
          {filtered.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => pick(opt)}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-soft"
            >
              <span className="text-deep">{opt}</span>
              {opt === value && <Check size={14} strokeWidth={1.75} className="text-brand" />}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onClick={() => pick(query.trim())}
              className="flex w-full items-center gap-1 border-t border-deep/10 px-3 py-1.5 text-left text-brand hover:bg-soft"
            >
              <Plus size={14} strokeWidth={1.75} />
              <span>
                Crear <strong>“{query.trim()}”</strong>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
